import React, { useState, useEffect, useRef } from 'react';
import { Bot, Sparkles, User, Bell, Crown, LogOut } from 'lucide-react';
import Chat from './components/Chat';
import Login from './components/auth/Login';
import ResetPassword from './components/auth/ResetPassword';
import PostList from './components/posts/PostList';
import { useAuth } from './contexts/AuthContext';
import { getPosts, createPost, updatePost, deletePost } from './lib/posts';
import type { Post } from './types/Post';

function App() {
  const { user, profile, loading, signOut } = useAuth();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [posts, setPosts] = useState<Post[]>([]);
  const [activeTab, setActiveTab] = useState<'chat' | 'posts'>('chat');
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadPosts();
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadPosts = async () => {
    try {
      const data = await getPosts();
      setPosts(data);
      setLoadError(null);
    } catch (error) {
      console.error('Error loading posts:', error);
      setLoadError('Erro ao carregar posts. Por favor, recarregue a página.');
    }
  };

  const handleAddPost = async (post: Omit<Post, 'id' | 'profile_id' | 'created_at' | 'updated_at'>) => {
    try {
      const newPost = await createPost(post);
      setPosts(prev => [newPost, ...prev]);
    } catch (error) {
      console.error('Error creating post:', error);
    }
  };

  const handleUpdatePost = async (id: string, updates: Partial<Post>) => {
    try {
      const updatedPost = await updatePost(id, updates);
      setPosts(prev => prev.map(post => post.id === id ? updatedPost : post));
    } catch (error) {
      console.error('Error updating post:', error);
    }
  };

  const handleDeletePost = async (id: string) => {
    try {
      await deletePost(id);
      setPosts(prev => prev.filter(post => post.id !== id));
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Check if we're on the reset password page
  if (window.location.pathname === '/reset-password') {
    return <ResetPassword />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 relative">
            <div className="absolute inset-0 rounded-full border-4 border-blue-200 opacity-25"></div>
            <div className="absolute inset-0 rounded-full border-4 border-blue-500 border-t-transparent animate-spin"></div>
          </div>
          <p className="text-gray-600 font-medium">Carregando...</p>
          {/* Add a refresh button if loading takes too long */}
          <button
            onClick={() => window.location.reload()}
            className="mt-4 text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Recarregar página
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {loadError && (
        <div className="bg-red-50 border-b border-red-200 p-3">
          <div className="flex items-center justify-between max-w-7xl mx-auto px-4">
            <p className="text-red-700">{loadError}</p>
            <button
              onClick={() => window.location.reload()}
              className="text-red-700 hover:text-red-800 font-medium"
            >
              Recarregar
            </button>
          </div>
        </div>
      )}

      {/* Top Navigation */}
      <nav className="flex-none bg-white border-b px-6 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <Sparkles className="w-6 h-6 text-purple-500" />
              <div className="absolute -top-1 -right-1 w-2 h-2 bg-purple-500 rounded-full animate-pulse" />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-purple-500 to-indigo-500 text-transparent bg-clip-text">
              Crie.ai
            </span>
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-4">
            {/* Pro Badge */}
            <button className="hidden sm:flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-full text-sm hover:from-amber-600 hover:to-amber-700 transition-all">
              <Crown className="w-4 h-4" />
              <span>Upgrade Pro</span>
            </button>

            {/* Notifications */}
            <div className="relative">
              <button 
                onClick={() => setShowNotifications(!showNotifications)}
                className="p-2 hover:bg-gray-100 rounded-full relative"
              >
                <Bell className="w-5 h-5 text-gray-600" />
                <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full" />
              </button>
              
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border py-2 z-50">
                  <div className="px-4 py-2 border-b">
                    <h3 className="font-semibold">Notificações</h3>
                  </div>
                  <div className="p-4 text-sm text-gray-500">
                    Nenhuma notificação no momento
                  </div>
                </div>
              )}
            </div>

            {/* Profile Menu */}
            <div className="relative" ref={profileMenuRef}>
              <button 
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-gray-600" />
                </div>
                <span className="text-sm font-medium hidden sm:block">{profile?.name}</span>
              </button>

              {showProfileMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border py-1 z-50">
                  <div className="px-4 py-2 border-b">
                    <div className="font-medium">{profile?.name}</div>
                    <div className="text-sm text-gray-500">{user.email}</div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Sair</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Desktop Layout */}
        <div className="hidden md:flex w-full">
          {/* Left Side - Chat */}
          <div className="w-1/2 flex flex-col border-r">
            <Chat assistantId="general" />
          </div>

          {/* Right Side - Posts */}
          <div className="w-1/2 flex flex-col bg-gray-50">
            <div className="h-full overflow-y-auto p-6">
              <PostList
                posts={posts}
                onAddPost={handleAddPost}
                onDeletePost={handleDeletePost}
                onUpdatePost={handleUpdatePost}
              />
            </div>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="flex flex-col md:hidden w-full">
          {activeTab === 'chat' ? (
            <div className="flex-1">
              <Chat assistantId="general" />
            </div>
          ) : (
            <div className="flex-1 bg-gray-50 overflow-y-auto p-4">
              <PostList
                posts={posts}
                onAddPost={handleAddPost}
                onDeletePost={handleDeletePost}
                onUpdatePost={handleUpdatePost}
              />
            </div>
          )}
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="md:hidden flex-none bg-white border-t">
        <div className="flex items-center">
          <button
            onClick={() => setActiveTab('chat')}
            className={`flex-1 py-2.5 text-center text-sm ${
              activeTab === 'chat' 
                ? 'text-purple-600 border-t-2 border-purple-600' 
                : 'text-gray-600'
            }`}
          >
            Chat
          </button>
          <button
            onClick={() => setActiveTab('posts')}
            className={`flex-1 py-2.5 text-center text-sm ${
              activeTab === 'posts' 
                ? 'text-purple-600 border-t-2 border-purple-600' 
                : 'text-gray-600'
            }`}
          >
            Posts
          </button>
        </div>
      </div>
    </div>
  );
}

export default App;